'use client';
// Les conventions de la période affichée, au-dessus de la grille du planning.
// Un repère : il ne crée aucune affectation. Silencieux quand il n'y a rien.
import React from 'react';
import {
  useOccurrences, parConvention, couleurDe, casesDuJour,
  type OccurrenceConvention, type PisteConvention,
} from '@/lib/conventions';

const JOURS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const court = (d: string) => `${JOURS[new Date(`${d}T12:00:00`).getDay()]} ${d.slice(8)}/${d.slice(5, 7)}`;

export function BandeauConventions({ du, au, occurrences }: { du: string; au: string; occurrences?: OccurrenceConvention[] }) {
  // Les occurrences peuvent venir de l'écran (qui les lit déjà pour ses repères) : pas de seconde requête.
  const lu = useOccurrences(occurrences ? '' : du, occurrences ? '' : au);
  const groupes = parConvention(occurrences ?? lu.occurrences);
  if (!groupes.length) return null;
  /* DISCRET (RQ, 04/10/2026) : une ligne de texte gris, un petit point de couleur
     par convention — plus de pastilles pleines. C'est un repère, pas une alerte :
     il ne doit pas concurrencer la grille qu'il accompagne. */
  return (
    <div role="note" aria-label="Conventions avec les cliniques sur la période"
      style={{ display: 'flex', gap: '2px 12px', flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0' }}>
      <span>Conventions :</span>
      {groupes.map((g) => {
        const quand = g.jours.length === 1 ? court(g.jours[0]!) : `${court(g.jours[0]!)} → ${court(g.jours[g.jours.length - 1]!)}`;
        return (
          <span key={g.conventionId} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Point couleur={g.couleur} />
            {g.nom} · {quand}{g.semaine ? ` · ${g.semaine}` : ''}
          </span>
        );
      })}
    </div>
  );
}

function Point({ couleur, taille = 6 }: { couleur: string | null; taille?: number }) {
  return <span aria-hidden style={{ width: taille, height: taille, borderRadius: '50%', flex: 'none', background: couleurDe(couleur).texte, opacity: 0.75 }} />;
}

/** Les conventions d'UN jour, en petits points sous la date (nom à l'infobulle).
 *  Rien quand le jour n'en porte pas. Réutilisé par le planning et le calendrier. */
export function PointsConventions({ liste, centre = false }: { liste?: OccurrenceConvention[]; centre?: boolean }) {
  if (!liste?.length) return null;
  const titre = liste.map((o) => `${o.nom}${o.semaine ? ` (${o.semaine})` : ''}`).join(' · ');
  return (
    <span title={`Convention : ${titre}`} aria-label={`Convention : ${titre}`} role="img"
      style={{ display: 'inline-flex', gap: 2, alignItems: 'center', justifyContent: centre ? 'center' : 'flex-start', verticalAlign: 'middle' }}>
      {liste.slice(0, 3).map((o) => <Point key={o.conventionId} couleur={o.couleur} taille={5} />)}
    </span>
  );
}

/**
 * Les conventions d'UN jour en FINES LIGNES de couleur, une par convention,
 * sous la date (RQ, 04/10/2026 : « une ligne de couleur pour chaque
 * convention »). Le nom est à l'infobulle. Pour les grilles du planning ; le
 * calendrier garde ses points.
 *
 * ── ⚠️ UNE LIGNE PAR PISTE, ET NON PAR CONVENTION PRÉSENTE (05/10/2026) ────
 *
 * Cette fonction empilait les conventions du jour. Trois le lundi, deux le
 * mardi : la clinique ambre passait de la 3e à la 2e ligne, et l'on ne pouvait
 * plus suivre une clinique du regard. « Il faut que ce soit dans l'ordre
 * toujours » (Inès).
 *
 * Elle reçoit donc désormais `pistes` — les conventions de toute la période
 * affichée, dans l'ordre du moteur — et rend UNE CASE PAR PISTE : colorée si la
 * convention tombe ce jour-là, TRANSPARENTE sinon. La règle est dans
 * `lib/conventions-pistes.ts`, éprouvée par `front-conventions-pistes`.
 *
 * ⚠️ `pistes` est FACULTATIF, et son absence fait retomber sur l'ancien
 * comportement. Ce n'est pas une porte laissée ouverte par confort : les deux
 * autres appelants (`MonPlanning`, le calendrier) utilisent les POINTS, pas les
 * lignes, et n'ont pas de notion de période à réserver. Le jour où ils passeront
 * aux lignes, ils devront passer les pistes — d'où le repli explicite plutôt
 * qu'un paramètre obligatoire qui aurait forcé à inventer une valeur.
 */
export function LignesConventions({ liste, largeur = '100%', pistes }: {
  liste?: OccurrenceConvention[];
  largeur?: number | string;
  pistes?: PisteConvention[];
}) {
  /* Rien ce jour-là : on ne pose RIEN. Réserver des lignes transparentes sur
     tous les jours creux du mois aurait épaissi chaque case pour ne rien
     montrer — la stabilité ne se paie pas en hauteur de grille. */
  if (!liste?.length) return null;
  const titre = liste.map((o) => `${o.nom}${o.semaine ? ` (${o.semaine})` : ''}`).join(' · ');
  const cases = pistes?.length
    ? casesDuJour(pistes, liste)
    : liste.map((o) => ({ piste: { conventionId: o.conventionId, nom: o.nom, couleur: o.couleur }, presente: true }));
  return (
    <span title={`Convention : ${titre}`} aria-label={`Convention : ${titre}`} role="img"
      style={{ display: 'flex', flexDirection: 'column', gap: 1, width: largeur, margin: '3px auto 0' }}>
      {cases.map(({ piste, presente }) => (
        <span key={piste.conventionId} style={{
          display: 'block', height: 3, borderRadius: 2,
          /* `transparent` et non une absence de nœud : la case VIDE doit
             occuper sa hauteur, sinon les suivantes remontent et l'on
             reproduit exactement le défaut corrigé. */
          background: presente ? couleurDe(piste.couleur).texte : 'transparent',
          opacity: presente ? 0.7 : 1,
        }} />
      ))}
    </span>
  );
}

/**
 * LA LÉGENDE DES COULEURS, sous la grille du mois (Inès, 05/10/2026).
 *
 * « Rajoute la légende explicative dans le mensuel au moins pour les codes
 *   couleurs des conventions. »
 *
 * Les fines lignes de couleur ne disent rien par elles-mêmes : il fallait
 * survoler une case pour apprendre à quelle clinique correspondait l'ambre. La
 * légende reprend les pistes DANS LE MÊME ORDRE que les lignes de la grille —
 * c'est ce qui permet de compter « troisième ligne = troisième entrée de la
 * légende » sans rien survoler.
 */
export function LegendeConventions({ pistes }: { pistes: PisteConvention[] }) {
  if (!pistes.length) return null;
  return (
    <div role="note" aria-label="Légende des couleurs de convention"
      style={{ display: 'flex', gap: '4px 14px', flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>
      <span>Conventions — une ligne par clinique, dans cet ordre :</span>
      {pistes.map((p, i) => (
        <span key={p.conventionId} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {/* Le même trait que dans la grille, pas une pastille : la légende
              doit montrer CE QU'ON VOIT dans les cases. */}
          <span aria-hidden style={{ display: 'block', width: 14, height: 3, borderRadius: 2, background: couleurDe(p.couleur).texte, opacity: 0.7 }} />
          <span>{i + 1}. {p.nom}</span>
        </span>
      ))}
    </div>
  );
}
